import type { PostedWithin, SearchPostingsQuery, WorkSetting } from "@/lib/api/catalogue";
import { areaSearch, type KnownArea } from "@/lib/envoyArea";
import type { JobLocation } from "@/lib/jobLocation";
import { POSTED_WINDOWS, WORK_SETTINGS } from "@/lib/openings";
import { DEFAULT_PAY_MIN, DEFAULT_PAY_MAX, PAY_CURRENCY, payAmount } from "@/lib/pay";
import { isUuid } from "@/lib/ids";
import type { CompanyRef } from "./company";

/** How many results a page holds. */
export const JOBS_PAGE = 20;

/**
 * The page's orders, as the catalogue sorts. Best match needs words to
 * match, and for a member with a resume is the most keywords covered,
 * from the resume service. Age is the newest posted first, by the board's date.
 */
export type JobsSort = "NEARBY" | "RELEVANCE" | "POSTED";

export const JOBS_SORT_LABEL: Record<JobsSort, string> = {
    NEARBY: "Near you",
    RELEVANCE: "Best match",
    POSTED: "Age",
};

/** Where the person's application stands, as a filter over the results. A skipped one is only in All. */
export type JobsStatus = "all" | "queue" | "applied" | "open";

export const JOBS_STATUSES: readonly JobsStatus[] = ["all", "queue", "applied", "open"];

export const JOBS_STATUS_LABEL: Record<JobsStatus, string> = {
    all: "All",
    queue: "In the queue",
    applied: "Applied",
    open: "Not yet added",
};

/** The words on each status tab. */
export const JOBS_STATUS_TAB: Record<JobsStatus, string> = {
    all: "All jobs",
    queue: "In queue",
    applied: "Applied",
    open: "Untracked",
};

/** What each status tab holds, in one sentence, for its tooltip. */
export const JOBS_STATUS_HINT: Record<JobsStatus, string> = {
    all: "Every job on this page, wherever it stands.",
    queue: "Jobs waiting in your queue to be tailored or sent.",
    applied: "Jobs you have sent an application to.",
    open: "Jobs you have not queued, applied to or skipped yet.",
};

/** The distances the page offers, in miles. */
export const WITHIN_MILES = [5, 25, 50] as const;

export type WithinMiles = (typeof WITHIN_MILES)[number];

export interface JobsView {
    q: string;
    sort: JobsSort | null;
    settings: WorkSetting[];
    posted: PostedWithin | null;
    /** How far from the person, in miles. None is any distance. */
    within: WithinMiles | null;
    status: JobsStatus;
    /** The languages a posting must be written in. None is any language. */
    languages: string[];
    payAtLeast: number | null;
    payAtMost: number | null;
    keepUnstatedPay: boolean;
    /** The posting open in the drawer. */
    posting: string | null;
    /** Every job of this company, from its own page. None is the whole catalogue. */
    company?: CompanyRef;
}

function oneOf<Value extends string>(
    value: string | null,
    allowed: readonly Value[],
): Value | null {
    return value !== null && (allowed as readonly string[]).includes(value)
        ? (value as Value)
        : null;
}

/** One of the distances the page offers, from the address; anything else is any distance. */
function withinOf(value: string | null): WithinMiles | null {
    const miles = Number(value);
    return (WITHIN_MILES as readonly number[]).includes(miles) ? (miles as WithinMiles) : null;
}

/** The words on each distance option. */
export const WITHIN_CHIP: Record<WithinMiles, string> = {
    5: "5 mi",
    25: "25 mi",
    50: "50 mi",
};

/** The address's word for "Any language", so it is not read as the browser's. */
const ANY_LANGUAGE = "any";

function sameLanguages(left: readonly string[], right: readonly string[]): boolean {
    return left.length === right.length && left.every((code, at) => code === right[at]);
}

/** The pay range a view opens on. */
const payFloor = (company?: CompanyRef) => (company ? null : DEFAULT_PAY_MIN);
const payCeiling = (company?: CompanyRef) => (company ? null : DEFAULT_PAY_MAX);

/**
 * The view from the address. What it does not know reads as unset, and
 * the languages, when it names none, as the ones the browser asks for.
 * One company's opens on every job it has, so it starts from no pay range.
 */
export function readJobsView(
    params: URLSearchParams,
    browser: readonly string[] = [],
    company?: CompanyRef,
): JobsView {
    const named = params.getAll("lang");
    const languages =
        named.length === 0
            ? [...browser]
            : named.filter((code) => /^[a-z]{2}$/.test(code) && code !== ANY_LANGUAGE);
    return {
        q: params.get("q")?.trim() ?? "",
        sort: oneOf(params.get("sort"), ["NEARBY", "RELEVANCE", "POSTED"] as const),
        settings: params
            .getAll("setting")
            .filter((value): value is WorkSetting =>
                (WORK_SETTINGS as readonly string[]).includes(value),
            ),
        posted: oneOf(params.get("posted"), POSTED_WINDOWS),
        within: withinOf(params.get("within")),
        status: oneOf(params.get("status"), JOBS_STATUSES) ?? "all",
        languages,
        payAtLeast: params.has("pay_min")
            ? payAmount(params.get("pay_min") ?? "")
            : payFloor(company),
        payAtMost: params.has("pay_max")
            ? payAmount(params.get("pay_max") ?? "")
            : payCeiling(company),
        keepUnstatedPay: params.get("pay_unstated") !== "false",
        // An id the services could never have given opens no drawer.
        posting: isUuid(params.get("posting")) ? params.get("posting") : null,
        ...(company && { company }),
    };
}

/** The view as the address carries it, leaving out what is unset or the browser's own. */
export function writeJobsView(view: JobsView, browser: readonly string[] = []): URLSearchParams {
    const params = new URLSearchParams();
    if (view.q) params.set("q", view.q);
    if (view.sort) params.set("sort", view.sort);
    for (const setting of view.settings) params.append("setting", setting);
    if (view.posted) params.set("posted", view.posted);
    if (view.within) params.set("within", String(view.within));
    if (view.status !== "all") params.set("status", view.status);
    if (!sameLanguages(view.languages, browser)) {
        if (view.languages.length === 0) params.set("lang", ANY_LANGUAGE);
        for (const code of view.languages) params.append("lang", code);
    }
    if (view.payAtLeast !== payFloor(view.company))
        params.set("pay_min", view.payAtLeast?.toString() ?? "any");
    if (view.payAtMost !== payCeiling(view.company))
        params.set("pay_max", view.payAtMost?.toString() ?? "any");
    if (!view.keepUnstatedPay) params.set("pay_unstated", "false");
    if (view.posting) params.set("posting", view.posting);
    return params;
}

/**
 * The orders the page offers, the one it opens on first: best match only
 * when there are words to match. One company's jobs open newest first,
 * and have no best match: it looks only near the person, and they are all
 * asked for.
 */
export function jobsSorts(view: Pick<JobsView, "q" | "company">): JobsSort[] {
    if (view.company) return ["POSTED", "NEARBY"];
    return view.q ? ["NEARBY", "RELEVANCE", "POSTED"] : ["NEARBY", "POSTED"];
}

/** The order in force: the one picked when it is offered, else the first offered. */
export function sortOf(view: Pick<JobsView, "q" | "sort" | "company">): JobsSort {
    const offered = jobsSorts(view);
    if (view.sort && offered.includes(view.sort)) return view.sort;
    return offered[0] ?? "NEARBY";
}

/** How far Best match looks for work done on site, in miles, when no distance is picked. */
export const BEST_MATCH_MILES = 50;

/**
 * Where Best match looks: hybrid and on-site work within the distance,
 * and remote work anywhere in the person's country. A member is measured
 * from their envoy's city, anyone else from where the edge says they
 * are. A place picked on the page stands as it is, Anywhere too, unless
 * a distance is picked with it.
 */
export function bestMatchNear(
    view: Pick<JobsView, "q" | "sort" | "within">,
    place: JobLocation | null,
    area: KnownArea | null,
): SearchPostingsQuery {
    if (sortOf(view) !== "RELEVANCE" || (place && view.within === null)) return {};
    const miles = view.within ?? BEST_MATCH_MILES;
    const measured = area ? areaSearch({ ...area, miles }) : null;
    if (!measured || !area) return { within_miles: miles };
    return { ...measured, near_country: area.country };
}

/** The catalogue search the view makes. The status is the page's own, over each page. */
export function jobsQuery(view: JobsView): SearchPostingsQuery {
    return {
        q: view.q || undefined,
        source: view.company?.source,
        company: view.company?.company,
        work_setting: view.settings.length > 0 ? view.settings : undefined,
        posted_within: view.posted ?? undefined,
        within_miles: view.within ?? undefined,
        language: view.languages.length > 0 ? view.languages : undefined,
        pay_currency:
            view.payAtLeast !== null || view.payAtMost !== null ? PAY_CURRENCY : undefined,
        pay_at_least: view.payAtLeast ?? undefined,
        pay_at_most: view.payAtMost ?? undefined,
        pay_unstated:
            view.payAtLeast !== null || view.payAtMost !== null ? view.keepUnstatedPay : undefined,
        sort: sortOf(view),
        limit: JOBS_PAGE,
    };
}

/** Whether anything Clear filters would clear is set. The status is the page's own. */
export function hasSearchFilters(view: JobsView): boolean {
    return (
        view.settings.length > 0 ||
        view.posted !== null ||
        view.within !== null ||
        view.languages.length > 0 ||
        view.payAtLeast !== null ||
        view.payAtMost !== null
    );
}

/** Whether any filter is set beside the words. */
export function hasFilters(view: JobsView): boolean {
    return hasSearchFilters(view) || view.status !== "all";
}

/** The words on each Posted option. */
export const POSTED_OPTION: Record<PostedWithin, string> = {
    "24h": "Last 24 hours",
    "3d": "3 days",
    "7d": "7 days",
};

/** The languages at the top of the Language filter; the rest are under More languages. */
export const QUICK_LANGUAGES: readonly (readonly [code: string, label: string])[] = [
    ["en", "English"],
    ["es", "Spanish"],
];

/** What Clear filters sets: every filter off, the words, the order and the status kept. */
export function clearedFilters(): Partial<JobsView> {
    return {
        settings: [],
        posted: null,
        within: null,
        languages: [],
        payAtLeast: null,
        payAtMost: null,
        keepUnstatedPay: true,
    };
}

/** The work settings with one turned on, or off when it was on. */
export function toggledSetting(
    settings: readonly WorkSetting[],
    setting: WorkSetting,
): WorkSetting[] {
    return settings.includes(setting)
        ? settings.filter((entry) => entry !== setting)
        : [...settings, setting];
}
